import { modal_html } from '../components/modal'
import { is_native_app, public_app_origin } from './runtime'
import { is_rede_mode, rede_display_name } from './rede-context'

// No app nativo o WebView do iOS roda em `capacitor://localhost` (não há iosScheme
// em capacitor.config.ts, então vale o default). Um href RELATIVO como
// "/privacidade.html" vira `capacitor://localhost/privacidade.html`; com
// target="_blank" o Capacitor delega pro sistema via UIApplication.open, e o iOS
// não tem handler pro esquema `capacitor:` — o link simplesmente não faz nada.
//
// Documento legal precisa abrir SEMPRE (App Store 1.5 / 5.1.1), então no nativo
// usamos a URL https pública, que abre no Safari com caminho de volta pro app.
// Na web mantemos o href relativo pra preservar o host atual (portais de rede).
export function legal_doc_href(path: string): string {
  return is_native_app() ? `${public_app_origin()}${path}` : path
}

// Rodapé das telas de entrada de telefone: um explicativo "Como funciona" (modal)
// e links para os documentos legais OFICIAIS do app.
//
// IMPORTANTE: Termos de Uso e Política de Privacidade NÃO são duplicados aqui — a
// fonte de verdade são os documentos public/termos-de-uso.html e
// public/privacidade.html. O servidor aplica a identidade do domínio e o visual
// do app, preservando o responsável e o CNPJ. Os links abrem em nova aba;
// target="_blank" também evita o interceptador de cliques em âncora do router.

export function como_funciona_marca(): string {
  return is_rede_mode() ? rede_display_name().replace(/[&<>"']/g, '') : 'NaBomba'
}

function como_funciona_html(): string {
  return `
  <div class="legal-doc p-2">
    <ol class="ps-3 mb-3">
      <li class="mb-2"><strong>Acesse o ${como_funciona_marca()}.</strong> Entre utilizando apenas o seu número de celular.</li>
      <li class="mb-2"><strong>Escolha a melhor oferta.</strong> Consulte os postos participantes e selecione o melhor preço para abastecer.</li>
      <li class="mb-2"><strong>Apresente o QR Code.</strong> Mostre o QR Code ao atendente antes de iniciar o abastecimento.</li>
      <li class="mb-2"><strong>Economize na hora!</strong> O desconto é aplicado no abastecimento e o pagamento é feito normalmente no próprio posto.</li>
    </ol>
    <div class="border-top pt-3 mt-3">
      <p class="fw-semibold mb-2"><i class="fas fa-lock text-primary me-1"></i>Sua privacidade é importante</p>
      <p class="text-muted small mb-0">Seu número de celular é usado apenas para confirmar seu acesso (por token) e para enviar campanhas promocionais que podem garantir ainda mais descontos. Não fazemos ligações nem enviamos mensagens desnecessárias.</p>
    </div>
  </div>`
}

export function open_como_funciona(): void {
  modal_html(como_funciona_html(), 'Como funciona', 560, 560, { modal_id: 'legal-como-funciona', size: 'lg' })
}

// Rodapé exibido sob o formulário de telefone. "Como funciona" abre o modal;
// Termos e Privacidade abrem as páginas oficiais em nova aba.
export function legal_links_html(): string {
  return `
    <div class="legal-links text-center mt-3 pt-3 border-top">
      <button type="button" class="btn btn-link btn-sm p-0 text-decoration-none link-body-emphasis" data-legal="como-funciona">Como funciona</button>
      <span class="text-muted small mx-1" aria-hidden="true">·</span>
      ${legal_documents_html()}
    </div>`
}

function legal_documents_html(): string {
  return `
    <a href="${legal_doc_href('/termos-de-uso.html')}" target="_blank" rel="noopener" class="btn btn-link btn-sm p-0 text-decoration-none link-body-emphasis">Termos de Uso</a>
    <span class="text-muted small mx-1" aria-hidden="true">·</span>
    <a href="${legal_doc_href('/privacidade.html')}" target="_blank" rel="noopener" class="btn btn-link btn-sm p-0 text-decoration-none link-body-emphasis">Política de Privacidade</a>`
}

export function render_legal_menu(): string {
  return `<button type="button" class="btn btn-link nav-link legal-menu px-2"
    data-legal="documentos" title="Termos e privacidade" aria-label="Termos e privacidade" aria-haspopup="dialog">
    <i class="fas fa-shield-halved" aria-hidden="true"></i>
  </button>`
}

function open_legal_documents(trigger: HTMLElement): void {
  if (document.getElementById('legal-documents')) return
  const docs = [
    { path: '/termos-de-uso.html', title: 'Termos de Uso', icon: 'fa-file-lines' },
    { path: '/privacidade.html', title: 'Política de Privacidade', icon: 'fa-shield-halved' },
  ]
  const instance = modal_html(`
    <div class="legal-choices">${docs.map((doc, index) => `
      <button type="button" data-document="${index}">
        <i class="fas ${doc.icon}" aria-hidden="true"></i><span>${doc.title}</span>
      </button>`).join('')}</div>
    <section class="legal-reader" hidden>
      <button type="button" class="btn btn-link" data-document-back><i class="fas fa-arrow-left" aria-hidden="true"></i> Voltar</button>
      <p data-document-status role="status"></p>
      <div class="document" data-document-content tabindex="0" aria-label="Documento"></div>
    </section>`, 'Termos e privacidade', 960, 900, { modal_id: 'legal-documents', size: 'lg' })
  const modal = document.getElementById('legal-documents')
  const choices = modal?.querySelector<HTMLElement>('.legal-choices')
  const reader = modal?.querySelector<HTMLElement>('.legal-reader')
  const content = modal?.querySelector<HTMLElement>('[data-document-content]')
  const status = modal?.querySelector<HTMLElement>('[data-document-status]')
  const back = modal?.querySelector<HTMLButtonElement>('[data-document-back]')
  if (!modal || !choices || !reader || !content || !status || !back) return
  modal.querySelector('.modal-title i')?.setAttribute('class', 'fas fa-shield-halved me-2')
  let request: AbortController | undefined
  let selected: HTMLButtonElement | undefined
  modal.addEventListener('shown.bs.modal', () => choices.querySelector<HTMLButtonElement>('button')?.focus(), { once: true })
  modal.addEventListener('hidden.bs.modal', () => {
    request?.abort()
    instance.dispose()
    trigger.focus({ preventScroll: true })
  }, { once: true })
  back.addEventListener('click', () => {
    request?.abort()
    content.replaceChildren()
    reader.hidden = true
    choices.hidden = false
    selected?.focus({ preventScroll: true })
  })
  choices.querySelectorAll<HTMLButtonElement>('button').forEach(button => {
    button.addEventListener('click', () => {
      const doc = docs[Number(button.dataset.document)]
      if (!doc) return
      selected = button
      request?.abort()
      const abort = new AbortController()
      request = abort
      choices.hidden = true
      reader.hidden = false
      status.textContent = 'Carregando…'
      content.replaceChildren()
      content.setAttribute('aria-label', doc.title)
      back.focus({ preventScroll: true })
      void fetch(legal_doc_href(doc.path), { signal: AbortSignal.any([abort.signal, AbortSignal.timeout(15_000)]), credentials: 'same-origin' })
        .then(async response => {
          if (!response.ok) throw new Error('Documento indisponível. Volte e tente novamente.')
          const page = new DOMParser().parseFromString(await response.text(), 'text/html')
          const article = page.querySelector('.document')
          if (!article) throw new Error('Documento indisponível. Volte e tente novamente.')
          if (abort.signal.aborted) return
          // Apenas o documento canônico do próprio app; nenhum HTML de cadastro.
          content.replaceChildren(...Array.from(article.childNodes).map(node => document.importNode(node, true)))
          status.textContent = ''
        })
        .catch(() => {
          if (!abort.signal.aborted) status.textContent = 'Não foi possível abrir o documento. Volte e tente novamente.'
        })
    })
  })
}

// O rodapé fica fora da área que cada página renderiza novamente. Documentos
// já presentes no formulário ou no menu do header não se repetem no rodapé.
export function mount_legal_footer(): void {
  const app = document.getElementById('app')
  if (!app) return
  let footer = document.getElementById('app-legal-footer')
  if (document.querySelector('#content-container .legal-links, #navbar .legal-menu')) {
    footer?.remove()
    return
  }
  if (!footer) {
    footer = document.createElement('footer')
    footer.id = 'app-legal-footer'
    footer.className = 'app-legal-footer'
    app.insertAdjacentElement('afterend', footer)
  }
  footer.innerHTML = `<nav aria-label="Documentos legais">${legal_documents_html()}</nav>`
}

// Liga o botão "Como funciona" ao modal. Chamar após injetar o innerHTML.
// (Termos/Privacidade são âncoras reais, não precisam de handler.)
export function bind_legal_links(root: ParentNode = document): void {
  root.querySelectorAll<HTMLElement>('[data-legal="como-funciona"]').forEach(button => {
    button.addEventListener('click', () => { open_como_funciona() })
  })
  root.querySelectorAll<HTMLElement>('[data-legal="documentos"]').forEach(button => {
    button.addEventListener('click', () => { open_legal_documents(button) })
  })
}
